import type { DemoTrajectoryEnding, DemoTrajectoryIncidentType } from '@wheere/api-tracker';

/**
 * ADR-045 — calculs purs des trajectoires de démo, partagés par Outils › Trajectoires de démo et
 * la colonne Trajectoire de Site › Équipements › Trackers. Le simulateur fait le même parcours côté
 * serveur (1 position/s) ; ici on ne s'en sert que pour afficher : longueur, durée, aperçu.
 */

/** Point WGS84 (même forme que `DemoTrajectoryPoint`). */
export interface LatLon {
  latitude: number;
  longitude: number;
}

export interface DemoIncident {
  type: DemoTrajectoryIncidentType;
  /** Secondes depuis le départ du parcours, pauses comprises. */
  atSeconds: number;
  /** Durée (NAN, PAUSE, SILENCE) ou âge de la position (STALE). */
  durationSeconds: number;
}

/** Ce qu'il faut d'une trajectoire pour la parcourir. */
export interface DemoCourse {
  points: readonly LatLon[];
  speedMps: number;
  ending: DemoTrajectoryEnding;
  incidents: readonly DemoIncident[];
}

/** Une trajectoire est proposée sur un site quand tous ses points sont à moins de 300 m de son antenne 0. */
export const DEMO_SITE_RADIUS_M = 300;

/** Durée d'affichage d'une position ancienne dans l'aperçu : le simulateur n'en envoie qu'une. */
const STALE_VISIBLE_S = 2;

const EARTH_RADIUS_M = 6_371_000;
const rad = (d: number) => (d * Math.PI) / 180;

/** Distance haversine en mètres. */
export function distanceMeters(a: LatLon, b: LatLon): number {
  const dLat = rad(b.latitude - a.latitude);
  const dLon = rad(b.longitude - a.longitude);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.sin(dLon / 2) ** 2;
  return EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

interface AnchorAntenna {
  orderGateway?: number;
  latitude?: number;
  longitude?: number;
}
interface AnchorGateway {
  orderSite?: number;
  antennas?: readonly AnchorAntenna[];
}

/**
 * Antenne 0 du site : gateway d'ordre 0, puis son antenne d'ordre 0. Même ordre plat que la carte
 * du site (`tracker-live-map`) : une antenne sans coordonnées est sautée. `null` si rien n'est placé.
 */
export function siteAnchor(gateways: readonly AnchorGateway[]): LatLon | null {
  const sorted = [...gateways].sort((a, b) => (a.orderSite ?? 0) - (b.orderSite ?? 0));
  for (const g of sorted) {
    const antennas = [...(g.antennas ?? [])].sort((x, y) => (x.orderGateway ?? 0) - (y.orderGateway ?? 0));
    for (const a of antennas) {
      if (Number.isFinite(a.latitude) && Number.isFinite(a.longitude)) {
        return { latitude: a.latitude!, longitude: a.longitude! };
      }
    }
  }
  return null;
}

/** Tous les points à moins de `radius` mètres de l'antenne 0 (bord compris). Faux sans point ou sans antenne. */
export function fallsOnSite(
  points: readonly LatLon[],
  anchor: LatLon | null,
  radius: number = DEMO_SITE_RADIUS_M
): boolean {
  if (!anchor || points.length === 0) return false;
  return points.every(p => distanceMeters(anchor, p) <= radius);
}

/** Nombre de points au-delà du rayon : l'avertissement de l'éditeur. */
export function pointsBeyond(
  points: readonly LatLon[],
  anchor: LatLon | null,
  radius: number = DEMO_SITE_RADIUS_M
): number {
  if (!anchor) return 0;
  return points.filter(p => distanceMeters(anchor, p) > radius).length;
}

/** Sites sur lesquels la trajectoire tombe, dans l'ordre reçu. */
export function sitesUnder<S extends { anchor: LatLon | null }>(points: readonly LatLon[], sites: readonly S[]): S[] {
  return sites.filter(s => fallsOnSite(points, s.anchor));
}

/**
 * Chemin réellement parcouru en un tour : la boucle revient au point 1 (à partir de trois points),
 * l'aller-retour revient par le même chemin, « une fois » s'arrête au dernier point.
 */
export function coursePath(points: readonly LatLon[], ending: DemoTrajectoryEnding): LatLon[] {
  if (ending === 'LOOP' && points.length > 2) return [...points, points[0]];
  if (ending === 'PINGPONG' && points.length > 1) return [...points, ...points.slice(0, -1).reverse()];
  return [...points];
}

export function courseLength(path: readonly LatLon[]): number {
  let total = 0;
  for (let i = 1; i < path.length; i++) total += distanceMeters(path[i - 1], path[i]);
  return total;
}

export interface CoursePlan {
  path: LatLon[];
  meters: number;
  /** Un tour, pauses comprises, arrondi à la seconde. */
  durationSeconds: number;
}

export function coursePlan(course: DemoCourse): CoursePlan {
  const path = coursePath(course.points, course.ending);
  const meters = courseLength(path);
  const pauses = course.incidents.filter(i => i.type === 'PAUSE').reduce((s, i) => s + i.durationSeconds, 0);
  const moving = course.speedMps > 0 ? meters / course.speedMps : 0;
  return { path, meters, durationSeconds: path.length > 1 ? Math.round(moving + pauses) : 0 };
}

/** Position à `meters` le long du chemin (bornée à ses extrémités). */
function alongPath(path: readonly LatLon[], meters: number): LatLon {
  if (path.length === 0) return { latitude: 0, longitude: 0 };
  let left = Math.max(0, meters);
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1];
    const b = path[i];
    const d = distanceMeters(a, b);
    if (left <= d) {
      const t = d > 0 ? left / d : 0;
      return {
        latitude: a.latitude + (b.latitude - a.latitude) * t,
        longitude: a.longitude + (b.longitude - a.longitude) * t,
      };
    }
    left -= d;
  }
  return path[path.length - 1];
}

export type DemoMode = 'move' | 'pause' | 'nan' | 'silence' | 'stale';

export interface DemoState {
  /** Où est le tracker (même pendant un silence ou des NaN : il continue d'avancer). */
  position: LatLon;
  /** Ce qu'il envoie : silence > NaN > position ancienne > pause > en route. */
  mode: DemoMode;
  /** Position ancienne envoyée (STALE) : là où il était `durationSeconds` plus tôt. */
  ghost: LatLon | null;
}

const within = (t: number, i: DemoIncident, span = i.durationSeconds) => t >= i.atSeconds && t < i.atSeconds + span;

/** Secondes de marche écoulées à l'instant `t` d'un tour (le temps des pauses ne fait pas avancer). */
function movingSeconds(pauses: readonly DemoIncident[], t: number): number {
  let paused = 0;
  for (const p of pauses) {
    if (t >= p.atSeconds + p.durationSeconds) paused += p.durationSeconds;
    else if (t > p.atSeconds) paused += t - p.atSeconds;
  }
  return Math.max(0, t - paused);
}

/**
 * État à l'instant `t` (secondes depuis le départ) pour l'aperçu. Au-delà d'un tour, la boucle et
 * l'aller-retour recommencent ; « une fois » reste au dernier point.
 */
export function courseStateAt(course: DemoCourse, t: number): DemoState {
  const plan = coursePlan(course);
  const local =
    plan.durationSeconds > 0 && course.ending !== 'ONCE'
      ? ((t % plan.durationSeconds) + plan.durationSeconds) % plan.durationSeconds
      : t;
  const pauses = course.incidents.filter(i => i.type === 'PAUSE').sort((a, b) => a.atSeconds - b.atSeconds);
  const moving = movingSeconds(pauses, local);
  const position = alongPath(plan.path, moving * course.speedMps);

  const active = (type: DemoTrajectoryIncidentType, span?: number) =>
    course.incidents.find(i => i.type === type && within(local, i, span));
  let mode: DemoMode = active('PAUSE') ? 'pause' : 'move';
  let ghost: LatLon | null = null;
  const stale = active('STALE', STALE_VISIBLE_S);
  if (stale) {
    mode = 'stale';
    ghost = alongPath(plan.path, Math.max(0, moving - stale.durationSeconds) * course.speedMps);
  }
  if (active('NAN')) mode = 'nan';
  if (active('SILENCE')) {
    mode = 'silence';
    ghost = null;
  }
  return { position, mode, ghost };
}

/** Où commence chaque incident, pour le repère sur la carte. */
export function incidentAnchors(course: DemoCourse): Array<DemoIncident & { position: LatLon }> {
  return course.incidents.map(i => ({ ...i, position: courseStateAt(course, i.atSeconds).position }));
}

/** « 45 s », « 2 min », « 1 min 05 s ». */
export function formatDemoDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (!m) return `${r} s`;
  return r ? `${m} min ${String(r).padStart(2, '0')} s` : `${m} min`;
}

/** « 850 m », « 1,25 km » (séparateur décimal de la langue). */
export function formatDemoDistance(meters: number, lang: string): string {
  if (meters < 1000) return `${Math.round(meters)} m`;
  const km = (meters / 1000).toFixed(2);
  return `${lang.startsWith('fr') ? km.replace('.', ',') : km} km`;
}
